import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import SourceHealthBadge from "../SourceHealthBadge";
import AdminBtn from "../layout/AdminBtn";
import {
  DATA_SOURCE_SCHEDULE_INTERVALS,
  intervalMinutesFromSource,
  schedulePayloadFromInterval,
} from "./dataSourceScheduleUtils";
import { lastResultSummary, statusGuide } from "./dataSourceManageCopy";

function intervalValue(minutes) {
  return minutes == null ? "" : String(minutes);
}

export default function EditDataSourceModal({
  source,
  canManage = false,
  isSaving = false,
  intervals = DATA_SOURCE_SCHEDULE_INTERVALS,
  canEditEnabled = false,
  canEditSchedule = false,
  scheduleBlockedReason = "",
  runBlockedReason = "",
  runHelp = "",
  viewRunsLabel = "Run history",
  deleteBlockedReason = "",
  saveError = "",
  canStop = false,
  stopBusy = false,
  extraActions = null,
  onClose,
  onSave,
  onRunNow,
  onViewRuns,
  onStop,
  onDelete,
}) {
  const displayName = source?.displayName || source?.sourceName || "this source";
  const sourceIdentity = source?.sourceKey || source?.sourceCode || source?.sourceId || "";
  const [enabled, setEnabled] = useState(Boolean(source?.enabled));
  const [intervalMinutes, setIntervalMinutes] = useState("");
  const [cronExpression, setCronExpression] = useState("");
  const [runImmediately, setRunImmediately] = useState(false);
  const [localSaveError, setLocalSaveError] = useState("");

  useEffect(() => {
    if (!source) return;
    setEnabled(Boolean(source.enabled));
    setIntervalMinutes(intervalValue(intervalMinutesFromSource(source)));
    setCronExpression(source.scheduleCronExpression || "");
    setRunImmediately(false);
    setLocalSaveError("");
    // Only reset draft fields when switching sources — not on every parent refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceIdentity]);

  useEffect(() => {
    setLocalSaveError(saveError || "");
  }, [saveError]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !isSaving) onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isSaving, onClose]);

  const scheduleOptions = useMemo(() => {
    const floor = Number(source?.minimumScheduleIntervalMinutes);
    const hasFloor = Number.isFinite(floor) && floor > 0;
    const options = intervals.filter(
      (item) => !hasFloor || item.minutes == null || item.minutes >= floor
    );
    const minutes = intervalMinutes === "" ? null : Number(intervalMinutes);
    if (minutes != null && !options.some((item) => item.minutes === minutes)) {
      options.push({ label: `Every ${minutes} minutes`, minutes });
    }
    return options;
  }, [intervalMinutes, intervals, source]);

  if (!source) return null;

  const currentMinutes = intervalMinutesFromSource(source);
  const schedulePayload = schedulePayloadFromInterval(
    intervalMinutes === "" ? null : Number(intervalMinutes),
    intervalMinutes !== ""
  );
  if (cronExpression.trim()) {
    schedulePayload.scheduleCronExpression = cronExpression.trim();
  }

  const enabledChanged = canEditEnabled && enabled !== Boolean(source.enabled);
  const scheduleChanged =
    canEditSchedule &&
    (Boolean(schedulePayload.scheduleEnabled) !== Boolean(source.scheduleEnabled) ||
      (schedulePayload.scheduleIntervalMinutes ?? null) !== (currentMinutes ?? null) ||
      cronExpression.trim() !== String(source.scheduleCronExpression || "").trim());
  const canStartImmediately = Boolean(enabled && (onRunNow || schedulePayload.scheduleEnabled));
  const dirty = enabledChanged || scheduleChanged || (canStartImmediately && runImmediately);
  const health = source.health || "unknown";
  const canSaveSettings = canEditEnabled || canEditSchedule;
  const displayError = localSaveError || saveError;

  const clearSaveError = () => {
    if (localSaveError) setLocalSaveError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!dirty || isSaving) return;
    clearSaveError();
    onSave?.({
      enabled,
      enabledChanged,
      scheduleChanged,
      schedulePayload: {
        ...schedulePayload,
        runImmediately: Boolean(canStartImmediately && runImmediately && schedulePayload.scheduleEnabled),
      },
      runImmediately: Boolean(canStartImmediately && runImmediately),
    });
  };

  return createPortal(
    <div className="admin-re-modal-backdrop" role="presentation" onClick={() => !isSaving && onClose?.()}>
      <div
        className="admin-re-modal panel-card admin-re-modal--wide admin-ds-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-data-source-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header admin-ds-edit-modal__header">
          <div className="admin-ds-edit-modal__title-block">
            <p className="eyebrow">
              {source.domainLabel ? `Data source · ${source.domainLabel}` : "Data source"}
            </p>
            <h2 id="edit-data-source-title">Manage {displayName}</h2>
            <div className="admin-ds-edit-modal__status-line">
              <SourceHealthBadge status={health} />
              <span>{statusGuide(health)}</span>
            </div>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose} disabled={isSaving}>
            Close
          </button>
        </header>

        <form className="admin-ds-edit-form" onSubmit={handleSubmit}>
          <div className="admin-ds-edit-scroll">
            <div className="admin-ds-edit-layout">
              <section className="admin-ds-edit-panel admin-ds-edit-panel--settings" aria-labelledby="ds-edit-settings-heading">
                <div className="admin-ds-edit-panel__intro">
                  <h3 id="ds-edit-settings-heading">Settings</h3>
                  <p className="admin-ds-edit-help">
                    These settings save together. Nothing updates until you click Save changes.
                  </p>
                </div>

                <div className="admin-ds-edit-field-block">
                  <h4>Collection</h4>
                  {canEditEnabled ? (
                    <fieldset className="admin-ds-choice-group">
                      <legend className="sr-only">Collection</legend>
                      <label className="admin-ds-choice">
                        <input
                          type="radio"
                          name="data-source-collection"
                          checked={enabled}
                          onChange={() => {
                            clearSaveError();
                            setEnabled(true);
                          }}
                        />
                        <span>
                          <strong>On</strong>
                          Collect data on a schedule or when you run it.
                        </span>
                      </label>
                      <label className="admin-ds-choice">
                        <input
                          type="radio"
                          name="data-source-collection"
                          checked={!enabled}
                          onChange={() => {
                            clearSaveError();
                            setEnabled(false);
                            setRunImmediately(false);
                          }}
                        />
                        <span>
                          <strong>Off</strong>
                          Stop scheduled and manual runs. Existing data stays.
                        </span>
                      </label>
                    </fieldset>
                  ) : (
                    <p className="admin-ds-edit-help">
                      {source.enabled
                        ? "Collection is on. This source does not have a separate on/off switch."
                        : "Collection is off."}{" "}
                      {canManage
                        ? "Use the schedule below if this source can run automatically."
                        : "You can review this source. Changing it requires permission to manage sources."}
                    </p>
                  )}
                </div>

                <div className="admin-ds-edit-field-block">
                  <h4>Schedule</h4>
                  {canEditSchedule ? (
                    <>
                      <p className="admin-ds-edit-help">
                        {source.minimumScheduleIntervalMinutes
                          ? "This source is on a limited cadence. Keep this pace or choose a slower one."
                          : enabled
                            ? "Choose how often it should collect. “Only when I start it” waits for Run now."
                            : "You can still choose a pace. It starts when collection is turned on."}
                      </p>
                      {source.scheduleBlocker && source.scheduleBlocker !== "none" ? (
                        <p className="admin-ds-edit-help">{source.scheduleBlocker}</p>
                      ) : null}
                      <label className="admin-re-modal-field">
                        <span>Collection pace</span>
                        <select
                          value={intervalMinutes}
                          onChange={(event) => {
                            clearSaveError();
                            const nextValue = event.target.value;
                            setIntervalMinutes(nextValue);
                            if (nextValue !== "" && !source.scheduleEnabled) {
                              setRunImmediately(true);
                            }
                          }}
                        >
                          {scheduleOptions.map((interval) => (
                            <option key={interval.label} value={intervalValue(interval.minutes)}>
                              {interval.minutes == null ? "Only when I start it" : interval.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      {enabled ? (
                        <label className="admin-ds-choice admin-ds-choice--checkbox">
                          <input
                            type="checkbox"
                            checked={runImmediately}
                            onChange={(event) => {
                              clearSaveError();
                              setRunImmediately(event.target.checked);
                            }}
                          />
                          <span>
                            <strong>Start the next collection now</strong>
                            Queue one run on save; later runs follow the pace above.
                          </span>
                        </label>
                      ) : null}
                      <details className="admin-ds-advanced" open={Boolean(source.scheduleCronExpression)}>
                        <summary>Custom cron schedule</summary>
                        <p className="admin-ds-edit-help">
                          Leave blank to use the pace above. Example: <code>0 */6 * * *</code> every six hours.
                        </p>
                        <label className="admin-re-modal-field">
                          <span>Cron expression</span>
                          <input
                            type="text"
                            value={cronExpression}
                            placeholder="Optional"
                            onChange={(event) => {
                              clearSaveError();
                              setCronExpression(event.target.value);
                            }}
                          />
                        </label>
                      </details>
                    </>
                  ) : (
                    <p className="admin-ds-edit-help">
                      {scheduleBlockedReason || "This source does not have a schedule you can change here."}
                    </p>
                  )}
                </div>
              </section>

              <aside className="admin-ds-edit-panel admin-ds-edit-panel--aside" aria-labelledby="ds-edit-aside-heading">
                <div className="admin-ds-edit-panel__intro">
                  <h3 id="ds-edit-aside-heading">Status and actions</h3>
                  <p className="admin-ds-edit-help">These actions run right away. They do not wait for Save.</p>
                </div>

                <div className="admin-re-modal-readonly admin-ds-edit-status-card">
                  <p className="admin-re-modal-status">
                    <SourceHealthBadge status={health} />
                  </p>
                  <p>{lastResultSummary(source)}</p>
                  {source.lastErrorMessage ? (
                    <p className="admin-ds-edit-error-text">{source.lastErrorMessage}</p>
                  ) : null}
                  {source.trustScore != null ? (
                    <p>
                      Trust {source.trustLabel || "score"} · {source.trustScore}
                      {source.topIssues?.[0] ? `. ${source.topIssues[0]}` : ""}
                    </p>
                  ) : null}
                  {source.sourceUrl ? (
                    <p>
                      <a href={source.sourceUrl} target="_blank" rel="noreferrer">
                        Open source website
                      </a>
                    </p>
                  ) : null}
                </div>

                <div className="admin-ds-edit-field-block">
                  <h4>Quick actions</h4>
                  {canStop ? (
                    <p className="admin-ds-edit-help">
                      A collection is in progress. Stop ends it at the next safe checkpoint.
                    </p>
                  ) : null}
                  {runBlockedReason && !canStop ? (
                    <p className="admin-ds-edit-help">{runBlockedReason}</p>
                  ) : null}
                  {!runBlockedReason && !canStop && runHelp ? (
                    <p className="admin-ds-edit-help">{runHelp}</p>
                  ) : null}
                  <div className="admin-ds-edit-actions">
                    {canStop && onStop ? (
                      <AdminBtn
                        variant="danger"
                        disabled={isSaving || stopBusy}
                        onClick={onStop}
                        title="Stop the in-progress sync at the next safe checkpoint"
                      >
                        {stopBusy ? "Stopping…" : "Stop sync"}
                      </AdminBtn>
                    ) : null}
                    {onRunNow && !canStop ? (
                      <AdminBtn
                        variant="primary"
                        disabled={Boolean(runBlockedReason) || isSaving}
                        onClick={onRunNow}
                      >
                        Run now
                      </AdminBtn>
                    ) : null}
                    {onViewRuns ? (
                      <AdminBtn variant="secondary" disabled={isSaving} onClick={onViewRuns}>
                        {viewRunsLabel}
                      </AdminBtn>
                    ) : null}
                    {extraActions}
                  </div>
                </div>

                {onDelete || deleteBlockedReason ? (
                  <div className="admin-ds-edit-danger">
                    <h4>Danger zone</h4>
                    {deleteBlockedReason ? (
                      <p className="admin-ds-edit-help">{deleteBlockedReason}</p>
                    ) : null}
                    {onDelete ? (
                      <AdminBtn
                        variant="danger"
                        disabled={isSaving || Boolean(deleteBlockedReason)}
                        onClick={onDelete}
                      >
                        Delete this source
                      </AdminBtn>
                    ) : null}
                  </div>
                ) : null}

                <details className="admin-ds-advanced">
                  <summary>Technical details</summary>
                  <p className="admin-ds-edit-help">
                    Key <code>{source.sourceKey}</code>
                    {source.sourceCode ? (
                      <>
                        {" "}
                        · Code <code>{source.sourceCode}</code>
                      </>
                    ) : null}
                  </p>
                  {(source.extraSourceLines || []).map((line) => (
                    <p key={line} className="admin-ds-edit-help">
                      {line}
                    </p>
                  ))}
                </details>
              </aside>
            </div>
          </div>

          <footer className="admin-re-modal-footer admin-ds-edit-modal__footer">
            {displayError ? (
              <p className="admin-ds-edit-save-error" role="alert">
                {displayError}
              </p>
            ) : !dirty && canSaveSettings ? (
              <p className="admin-ds-edit-footer-note">No changes to save</p>
            ) : (
              <span className="admin-ds-edit-footer-spacer" />
            )}
            <div className="admin-ds-edit-footer-actions">
              <button type="button" className="midpoint-share-button" onClick={onClose} disabled={isSaving}>
                Cancel
              </button>
              {canSaveSettings ? (
                <button
                  type="submit"
                  className="midpoint-share-button admin-re-btn-primary"
                  disabled={!dirty || isSaving || !canManage}
                >
                  {isSaving ? "Saving…" : "Save changes"}
                </button>
              ) : null}
            </div>
          </footer>
        </form>
      </div>
    </div>,
    document.body
  );
}
